export interface PricingPlan {
  key: string;
  name: string;
  price: number;
  displayPrice: string;
  period: string;
  description: string;
  days: number;
  matchLimit: number;
  /** Monthly AI message quota. null = unlimited. */
  aiMessagesPerMonth: number | null;
  features: string[];
}

export const PLAN_PRICING = {
  playtest: {
    key: 'playtest',
    name: 'Starter (Playtest)',
    price: 0,
    displayPrice: '$0.00',
    period: '',
    description: 'Playtest access',
    days: 3,
    matchLimit: 5,
    aiMessagesPerMonth: 10,
    features: [],
  },
  starter: {
    key: 'starter',
    name: 'Starter',
    price: 39,
    displayPrice: '$39',
    period: '',
    description: 'Hook + trust',
    days: 30,
    matchLimit: 10,
    aiMessagesPerMonth: 50,
    features: [
        '10 high-fit investor matches',
        'Limited insights',
        'Basic filtering',
        'Warm intros',
        '50 AI messages/month',
    ],
  },
  fundraise: {
    key: 'fundraise',
    name: 'Fundraise Plan',
    price: 129,
    displayPrice: '$129',
    period: '',
    description: 'This is your core product',
    days: 30,
    matchLimit: 25,
    aiMessagesPerMonth: 300,
    features: [
        '25-50 high-fit investor matches',
        'Detailed "why this investor" insights',
        'Filtering by stage, check size, and industry',
        'Export and outreach support',
        'Investor network & pipeline',
        '300 AI messages/month',
    ],
  },
  pro: {
    key: 'pro',
    name: 'Pro Plan',
    price: 199,
    displayPrice: '$199',
    period: '',
    description: 'For serious founders actively raising',
    days: 30,
    matchLimit: 50,
    aiMessagesPerMonth: null,
    features: [
        '100+ high-fit investor matches',
        'Advanced filters',
        'Priority and "hot investor" signals',
        'Ongoing updates with new investors added weekly',
        'Investor network & pipeline',
        'Unlimited AI messages',
    ],
  },
  // Yearly plans — 25% discount per month × 12 months (monthly price × 0.75 × 12).
  // Edit `price` and `displayPrice` here to change what the pricing page shows.
  // The pricing page derives the big "monthly equivalent" number from `price / 12`.
  starter_yearly: {
    key: 'starter_yearly',
    name: 'Starter (Yearly)',
    price: 351,
    displayPrice: '$351',
    period: '/year',
    description: 'Hook + trust',
    days: 365,
    matchLimit: 10,
    aiMessagesPerMonth: 50,
    features: [
        '10 high-fit investor matches',
        'Limited insights',
        'Basic filtering',
        'Warm intros',
        '50 AI messages/month',
    ],
  },
  fundraise_yearly: {
    key: 'fundraise_yearly',
    name: 'Fundraise Plan (Yearly)',
    price: 1161,
    displayPrice: '$1,161',
    period: '/year',
    description: 'This is your core product',
    days: 365,
    matchLimit: 25,
    aiMessagesPerMonth: 300,
    features: [
        '25-50 high-fit investor matches',
        'Detailed "why this investor" insights',
        'Filtering by stage, check size, and industry',
        'Export and outreach support',
        'Investor network & pipeline',
        '300 AI messages/month',
    ],
  },
  pro_yearly: {
    key: 'pro_yearly',
    name: 'Pro Plan (Yearly)',
    price: 1791,
    displayPrice: '$1,791',
    period: '/year',
    description: 'For serious founders actively raising',
    days: 365,
    matchLimit: 50,
    aiMessagesPerMonth: null,
    features: [
        '100+ high-fit investor matches',
        'Advanced filters',
        'Priority and "hot investor" signals',
        'Ongoing updates with new investors added weekly',
        'Investor network & pipeline',
        'Unlimited AI messages',
    ],
  },
} as const satisfies Record<string, PricingPlan>;

export const PAYMENT_PLANS: PricingPlan[] = [
  PLAN_PRICING.starter,
  PLAN_PRICING.fundraise,
  PLAN_PRICING.pro,
  PLAN_PRICING.starter_yearly,
  PLAN_PRICING.fundraise_yearly,
  PLAN_PRICING.pro_yearly,
];

export const ADMIN_PLAN_OPTIONS: PricingPlan[] = [
  PLAN_PRICING.playtest,
  PLAN_PRICING.starter,
  PLAN_PRICING.fundraise,
  PLAN_PRICING.pro,
  PLAN_PRICING.starter_yearly,
  PLAN_PRICING.fundraise_yearly,
  PLAN_PRICING.pro_yearly,
];

export const PLAN_INDEX_BY_KEY: Record<string, number> = {
  starter: 0,
  fundraise: 1,
  pro: 2,
  starter_yearly: 3,
  fundraise_yearly: 4,
  pro_yearly: 5,
};